import { useMemo, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import get from 'lodash/get';

import { Message } from '@components';
import { useRelatedMinMaxValidation } from '@hooks';
import { DatamodelFormField, FormError } from '@Forms/fields.jsx';
import { errorsFromMutate } from '@Forms/utils.js';
import { ButtonSpinnerIcon } from '@svgs';

export const FallbacksForm = ({
  datamodelData,
  disabled,
  mutation,
  relatedDatamodels,
  siteData,
}) => {
  const useFormReturn = useForm({
    defaultValues: siteData,
    mode: 'onBlur',
  });
  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    watch,
    formState: { errors },
  } = useFormReturn;
  const { isDirty, isValid } = useFormState({ control });

  const [formErrorCount, setFormErrorCount] = useState(0);

  const relatedModelListFields = useMemo(() => {
    return (datamodelData?.fields || [])
      .filter((field) => field.field_type !== 'boolean')
      .filter((field) => field.field_type === 'related_model_list')
      .map((field) => ({
        slug: field.slug,
        required: !!field.required,
        min_items: field.min_items,
        max_items: field.max_items,
      }));
  }, [datamodelData?.fields]);

  useRelatedMinMaxValidation({
    fields: relatedModelListFields,
    namePrefix: 'custom_fields',
    watch,
    errors,
    setError,
    clearErrors,
  });

  const onSubmit = (data) => {
    // payload is just custom fields to avoid conflicting saves
    const payload = {
      custom_fields: data.custom_fields,
    };
    mutation.mutate(payload, {
      onError: (error) => {
        // handle server-side validations and errors
        const errorsList = errorsFromMutate(error);
        errorsList.forEach(({ field, error }) => setError(field, error));
      },
    });
  };

  // Handles submit errors
  const onError = (errors, e) => {
    setFormErrorCount(formErrorCount + 1);
    console.log(errors, e);
  };

  // list of dynamic fields
  // prefix added to target custom_fields.
  //
  // need to filter out boolean fields because they do
  // not support fallbacks/defaults
  const fieldsList = datamodelData.fields
    .filter((field) => field.field_type != 'boolean')
    .map((field) => {
      const fieldName = `custom_fields.${field.slug}`;
      const fieldError = get(errors, fieldName);
      return (
        <div key={field.id}>
          <DatamodelFormField
            datamodelField={field}
            fieldPrefix={`custom_fields.`}
            relatedDatamodels={relatedDatamodels}
            siteID={siteData.id}
            useFormReturn={useFormReturn}
          />
          {field.field_type === 'related_model_list' && fieldError?.message && (
            <FormError message={fieldError.message} />
          )}
        </div>
      );
    });

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className={
        `sb-manage-content-form ` +
        (mutation.isPending ? 'sb-form-disabled' : '')
      }
    >
      <fieldset className="sb-form-item">
        <div className="sb-form-item-inner">
          <div className="sb-form-item-elements">{fieldsList}</div>
        </div>
      </fieldset>
      <div className="sb-manage-content-button-bar">
        <div className="sb-flex">
          <button
            type="submit"
            className="sb-btn sb-btn-save"
            disabled={disabled}
          >
            {mutation.isPending ? 'Publishing...' : 'Publish changes'}
            {mutation.isPending && <ButtonSpinnerIcon />}
          </button>
        </div>
        {!isValid && isDirty && (
          <Message
            rerenderCounter={formErrorCount}
            messageStyle="error"
            title="Please review your form"
          />
        )}
        {mutation.isError && (
          <Message
            messageStyle="error"
            title="Error saving changes"
            message="Please review your form"
          />
        )}
        {mutation.isSuccess && (
          <Message
            messageStyle="success"
            title="Successfully published"
          ></Message>
        )}
      </div>
    </form>
  );
};
